import axios from "axios";

/**
 * ESC/POS printer registry against /api/print-admin.
 *
 * This is the table print_worker drains and PrintService.create_kot_jobs routes
 * from. The Admin panel used to write printers into IndexedDB and queue an
 * UPDATE_PRINTERS sync action the server rejects, so a printer "saved" there was
 * never a printer the kitchen could print to.
 */
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/print-admin`;

const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

export const listPrinters = token =>
  axios.get(`${API}/printers`, auth(token)).then(r => r.data);

export const createPrinter = (body, token) =>
  axios.post(`${API}/printers`, body, auth(token)).then(r => r.data);

export const updatePrinter = (printerId, patch, token) =>
  axios.put(`${API}/printers/${printerId}`, patch, auth(token)).then(r => r.data);

/**
 * Hard delete. The server refuses (400) once the printer has any print history —
 * disable is the soft-delete path for a printer that has ever printed.
 */
export const deletePrinter = (printerId, token) =>
  axios.delete(`${API}/printers/${printerId}`, auth(token)).then(r => r.data);

export const enablePrinter = (printerId, token) =>
  axios.patch(`${API}/printers/${printerId}/enable`, null, auth(token)).then(r => r.data);

export const disablePrinter = (printerId, token) =>
  axios.patch(`${API}/printers/${printerId}/disable`, null, auth(token)).then(r => r.data);

/** Enqueues a real commissioning slip through PrintService — returns its job id. */
export const testPrint = (printerId, token) =>
  axios.post(`${API}/printers/${printerId}/test`, null, auth(token)).then(r => r.data);

/** Menu item ids whose KOT lines route to this printer (menu_item_printers). */
export const printerMenuItems = (printerId, token) =>
  axios.get(`${API}/printers/${printerId}/menu-items`, auth(token)).then(r => r.data);

/** Table ids whose bill prints here (tables.bill_printer_id). */
export const printerTables = (printerId, token) =>
  axios.get(`${API}/printers/${printerId}/tables`, auth(token)).then(r => r.data);

/** User ids whose bills are locked to this printer (users.bill_printer_id). */
export const printerUsers = (printerId, token) =>
  axios.get(`${API}/printers/${printerId}/users`, auth(token)).then(r => r.data);

/**
 * Printers, the menu grouped by category with each item's printer set, tables,
 * staff, and the RESOLVED default printer — one read, because the routing editor
 * has to show the three axes against each other and a per-printer fan-out could
 * paint half a screen of stale assignments.
 */
export const routingCatalog = token =>
  axios.get(`${API}/routing`, auth(token)).then(r => r.data);

/**
 * The three setters below each REPLACE this printer's whole set: an id left out
 * is unassigned, not merely untouched. That is the server's contract and it is
 * what lets a checkbox grid be saved as-is.
 *
 * Items may route to several printers at once (the KOT fans out one job each), so
 * setPrinterMenuItems rewrites only this printer's column of the matrix.
 */
export const setPrinterMenuItems = (printerId, menuItemIds, token) =>
  axios.put(`${API}/printers/${printerId}/menu-items`, { menu_item_ids: menuItemIds }, auth(token)).then(r => r.data);

export const setPrinterTables = (printerId, tableIds, token) =>
  axios.put(`${API}/printers/${printerId}/tables`, { table_ids: tableIds }, auth(token)).then(r => r.data);

/** Assigning also sets bill_printer_locked; clearing releases it. */
export const setPrinterUsers = (printerId, userIds, token) =>
  axios.put(`${API}/printers/${printerId}/users`, { user_ids: userIds }, auth(token)).then(r => r.data);

// Bill logo (b9d5e7c2f184): outlet-level, lives with the printer surfaces
// because that is where an owner configures paper output.
export const uploadBillLogo = (imageB64, token) =>
  axios.post(`${API.replace("/print-admin", "/settings")}/bill-logo`, { image_b64: imageB64 }, auth(token))
    .then(res => res.data);

// ── Print jobs and printer alerts (settings sweep, 2026-10-19) ────────────────
export const listPrintJobs = (status, token, limit = 50) =>
  axios.get(`${API}/jobs`, { ...auth(token), params: { ...(status ? { status } : {}), limit } }).then(r => r.data);

export const retryPrintJob = (jobId, token) =>
  axios.post(`${API}/jobs/${jobId}/force-retry`, {}, auth(token)).then(r => r.data);

export const listPrinterEvents = (token, unseenOnly = true) =>
  axios.get(`${API}/events`, { ...auth(token), params: { unseen_only: unseenOnly } }).then(r => r.data);

export const markPrinterEventSeen = (eventId, token) =>
  axios.patch(`${API}/events/${eventId}/seen`, {}, auth(token)).then(r => r.data);

// One tap for a printer's whole run of alerts (or every printer's, without an id).
export const markPrinterEventsSeen = (printerId, token) =>
  axios.patch(`${API}/events/seen`, {}, { ...auth(token), params: printerId != null ? { printer_id: printerId } : {} }).then(r => r.data);
